import { CommonModule } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { DividerModule } from 'primeng/divider';
import { CardModule } from 'primeng/card';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { BarcodeFormat } from '@zxing/library'; // Wird für allowedFormats benötigt
import { ZXingScannerModule } from '@zxing/ngx-scanner';
import { BarcodeScannerService } from '../../../../services/barcode-scanner.service';
import { CalendarModule } from 'primeng/calendar';
import { ChargeService } from '../../../../services/charge.service';
import { CustomResponse } from '../../../../interfaces/custom-response.model';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { Router } from '@angular/router';

@Component({
  selector: 'app-charge-scan',
  standalone: true,
  imports: [DividerModule, CommonModule, CardModule, InputGroupModule, InputGroupAddonModule, InputTextModule, ButtonModule, ReactiveFormsModule, ToastModule, ZXingScannerModule, CalendarModule],
  templateUrl: './charge-scan.component.html',
  styleUrl: './charge-scan.component.css',
  providers: [
    MessageService
  ]
})
export class ChargeScanComponent implements OnInit {

  loading: boolean = false;
  chargeForm!: FormGroup;
  fb = inject(FormBuilder);
  scannerService = inject(BarcodeScannerService);
  chargeService = inject(ChargeService);
  messageService = inject(MessageService);
  router = inject(Router);

  prevals = {
    banr: '',
    mitarbeiternr: '',
    datum: new Date(),
    fertigungsmenge: '',
    materialnr: '',
    material_batchnr: ''
  }

  allowedFormats: BarcodeFormat[]; // Vom Service holen

  constructor() {
    this.allowedFormats = this.scannerService.allowedFormats;
  }

  ngOnInit(): void {
    this.resetForm();
    document.getElementById('banr')?.focus();
  }

  resetForm() {
    this.chargeForm = this.fb.group({
      // Add Validators.required to all fields
      banr: [this.prevals.banr, Validators.required],
      mitarbeiternr: [this.prevals.mitarbeiternr, Validators.required],
      datum: [new Date(), Validators.required], // Keep current date initialization
      // Use Validators.compose for multiple validators
      fertigungsmenge: [this.prevals.fertigungsmenge, [Validators.required, Validators.pattern("^[0-9]*$")]],
      materialnr: ['', Validators.required],
      material_batchnr: ['', Validators.required]
    });
  }

  startScanFor(targetControlName: string): void {
    this.scannerService.startScan(targetControlName);
  }

  stopScan(): void {
    this.scannerService.stopScan();
  }

  changeDevice(event: Event): void {
    const selectElement = event.target as HTMLSelectElement;
    this.scannerService.changeDevice(selectElement.value);
  }

  onCamerasFoundHandler(devices: MediaDeviceInfo[]): void {
    this.scannerService.reportCamerasFound(devices);
    // Kein cdRef.markForCheck() mehr nötig
  }

  onPermissionResponseHandler(hasPermission: boolean): void {
    this.scannerService.reportPermissionResult(hasPermission);
  }

  onScanSuccessHandler(resultString: string): void {
    // Direkt das Formular aktualisieren, wenn der Service den Erfolg meldet
    const currentTarget = this.scannerService.currentScanTarget(); // Aktuelles Ziel vom Service holen
    if (currentTarget) {
      console.log(`Component: Received scan success "${resultString}" for target "${currentTarget}"`);
      this.chargeForm.get(currentTarget)?.setValue(resultString);
    }
    // Service kümmert sich um das Melden und Stoppen
    this.scannerService.reportScanSuccess(resultString);
  }

  onScanErrorHandler(error: Error): void {
    this.scannerService.reportScanError(error);
  }

  isControlInvalid(controlName: string): boolean {
    const control = this.chargeForm.get(controlName);
    return !!control && control.invalid && (control.dirty || control.touched);
  }

  saveAndClose(): void {
    if (this.chargeForm.valid) {
      console.log('Form Submitted:', this.chargeForm.value);
      
      this.saveCharge();
      
      this.router.navigate(['dashboard']);
      

    } else {
      console.error('Form is invalid');
      // Mark all fields as touched to display validation errors
      this.chargeForm.markAllAsTouched();
    }
  }

  saveCharge() {
    this.prevals.banr = this.chargeForm.get('banr')?.value || '';
    this.prevals.mitarbeiternr = this.chargeForm.get('mitarbeiternr')?.value || '';
    this.prevals.datum = this.chargeForm.get('datum')?.value || new Date();
    this.prevals.fertigungsmenge = this.chargeForm.get('fertigungsmenge')?.value || '';
    this.prevals.materialnr = '';
    this.prevals.material_batchnr = '';

    console.log("Daten werden gespeichert ...");

    this.chargeService.storeCharge(this.chargeForm.value).subscribe({
      next: (data: CustomResponse) => {
        console.log(data.success);
        if (data.success == 1) {
          this.messageService.add({ key: 'tc', severity: 'success', summary: 'Hinweis', detail: 'Charge wurde gespeichert!' + data.message })
          this.resetForm();
        } else {
          this.messageService.add({ key: 'tc', severity: 'error', summary: 'Fehler', detail: 'Daten konnten nicht gespeichert werden: ' + data.message })
        }
      }
    });
  }

  saveAndScanNew(): void {
    if (this.chargeForm.valid) {
      console.log('Form Submitted (Save & New):', this.chargeForm.value);
      // Add logic to send data to backend
      // Reset the form for the next scan

      this.saveCharge();

      // Optionally focus the first field
      document.getElementById('banr')?.focus();
    } else {
      console.error('Form is invalid');
      this.chargeForm.markAllAsTouched();
    }
  }

}
